// src/components/orders/PayExpressCodeModal.js
// ============================================================
// CODE PAYEXPRESS — a remettre au client qui n'a pas encore paye
// ------------------------------------------------------------
// Cas d'usage : le commercial cree la commande au guichet, le client
// ne paie pas sur place. Il repart avec un CODE PAYEXPRESS qu'il
// presente dans une agence Cash Plus / Barid Cash / Damane Cash /
// Lana Cash. Des que l'agence encaisse, Maxula bascule la commande en
// payee et emet les coupons — personne n'a a y revenir.
//
// TROIS PRINCIPES TENUS ICI
// 1. La commande est DEJA CREEE quand cet ecran s'ouvre. Si le code
//    echoue, on le dit sans laisser croire que la commande est perdue :
//    sinon l'agent la ressaisit et le client se retrouve avec deux
//    commandes pour un seul achat.
// 2. Aucun code n'est fabrique localement. Un code invente serait
//    refuse au comptoir : le client se deplacerait pour rien.
// 3. Le message dit « PAYEXPRESS » et jamais « Payzone ». C'est
//    l'enseigne affichee sur le terminal ; les agents ne connaissent
//    pas l'autre nom et ne trouvent pas l'operation.
//
// Maxula — AbodyStudio Limited
// ============================================================

import React, { useEffect, useState } from 'react';
import {
  FiX, FiCopy, FiSend, FiLoader, FiAlertTriangle, FiCheckCircle,
  FiFileText, FiRefreshCw, FiClock
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { orderAPI } from '../../services/api';
import { waNumber, waLink } from '../../utils/whatsapp';

const AGENCES = 'Cash Plus, Barid Cash, Damane Cash ou Lana Cash';

export default function PayExpressCodeModal({
  orderId,
  orderNumber,
  amount = 0,
  customer = {},
  onClose
}) {
  // L'ecran de creation connait l'id numerique, l'ecran de partage ne connait
  // que le numero de commande. Le serveur accepte les deux ; on prend ce qu'on a.
  const ref = orderId != null && orderId !== '' ? orderId : orderNumber;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState(customer.phone || '');
  const [email, setEmail] = useState(customer.email || '');
  const [sending, setSending] = useState(false);

  const ask = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await orderAPI.payexpressCode(ref);
      const d = res?.data?.data || null;
      setData(d);
      // Le serveur complete les coordonnees depuis le compte client quand la
      // commande y est rattachee : on ne les ecrase que si les champs sont
      // encore vides, pour ne pas effacer ce que l'agent vient de corriger.
      setPhone((p) => p || d?.customer?.phone || '');
      setEmail((e) => e || d?.customer?.email || '');
    } catch (e) {
      const r = e?.response?.data || {};
      setError({
        code: r.code || null,
        message: r.message || "Le code PayExpress n'a pas pu etre genere.",
        details: Array.isArray(r.details) ? r.details : []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ref) {
      setLoading(false);
      setError({ code: 'NO_ORDER', message: 'Commande introuvable.', details: [] });
      return;
    }
    ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const code = data?.code || '';
  const total = Number(data?.amount || amount || 0);
  const num = data?.orderNumber || orderNumber || '';

  const message = [
    `Bonjour${customer.name ? ' ' + customer.name : ''},`,
    ``,
    `Votre commande Maxula ${num} est reservee.`,
    `Montant a regler : ${total.toFixed(2)} MAD`,
    ``,
    `CODE DE PAIEMENT PAYEXPRESS : ${code}`,
    ``,
    `Ou payer : agence ${AGENCES}.`,
    `A dire a l'agent : « paiement PAYEXPRESS » (et non Payzone).`,
    ``,
    `Votre commande sera confirmee automatiquement des reception du paiement.`
  ].join('\n');

  const copyCode = () => {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      toast.success('Code copie');
    } catch (e) {
      toast.error('Copie impossible — notez le code a la main');
    }
  };

  const openWhatsApp = () => {
    const n = waNumber(phone);
    if (phone && !n) {
      toast.error('Numero invalide — format attendu : 212XXXXXXXXX');
      return;
    }
    window.open(waLink(n, message), '_blank', 'noopener');
  };

  const sendByEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())) {
      toast.error('Adresse email invalide');
      return;
    }
    setSending(true);
    try {
      const res = await orderAPI.sendPayexpressCode(ref, { email: email.trim() });
      toast.success(res?.data?.message || 'Code envoye par email');
    } catch (e) {
      toast.error(e?.response?.data?.message || "Echec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold">Code PayExpress</h2>
            <p className="text-sm text-gray-500 font-mono">{num}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Fermer">
            <FiX size={22} />
          </button>
        </div>

        {/* La commande existe DEJA, quel que soit le sort du code. C'est la
            premiere chose a lire : sans elle, un echec de code pousse a
            ressaisir la commande. */}
        <div className="mx-5 mt-5 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <FiCheckCircle className="mt-0.5 shrink-0" size={16} />
          <div>
            <strong>Commande enregistree</strong> — non payee, en attente du reglement
            en agence. Ne la ressaisissez pas.
          </div>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
            <FiLoader className="animate-spin" /> Generation du code…
          </div>
        ) : error ? (
          <div className="p-5 space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <FiAlertTriangle className="mt-0.5 shrink-0" size={16} />
                <div>
                  <strong>Code non genere.</strong> {error.message}
                  {error.details.length > 0 && (
                    <ul className="mt-2 list-disc pl-5 text-xs">
                      {error.details.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Que faire maintenant : encaissez le client par un autre moyen et
              marquez la commande payee depuis la liste des commandes, ou
              reessayez une fois PayExpress configure.
              {error.code === 'PAYEXPRESS_UNAVAILABLE'
                ? " Les variables ci-dessus se renseignent dans le fichier .env du serveur, puis il faut redemarrer."
                : ''}
            </p>
            <div className="flex flex-wrap gap-2 border-t pt-4">
              <button
                type="button"
                onClick={ask}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
              >
                <FiRefreshCw size={15} /> Reessayer
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-semibold hover:opacity-90"
              >
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* LE CODE — c'est la seule chose que le client doit retenir, il
                occupe donc la place correspondante a l'ecran. */}
            <div className="rounded-xl border-2 border-[#e11d7b] bg-[#fff5fa] px-4 py-5 text-center">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Code de paiement PayExpress
              </div>
              <div className="my-2 font-mono text-3xl font-extrabold tracking-[0.15em] text-[#e11d7b] break-all">
                {code || '—'}
              </div>
              <div className="text-sm text-gray-700">
                Montant a regler : <strong>{total.toFixed(2)} MAD</strong>
              </div>
              <button
                type="button"
                onClick={copyCode}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
              >
                <FiCopy size={13} /> Copier le code
              </button>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <strong>A dire au client :</strong> payer dans une agence {AGENCES},
              en demandant un paiement <strong>PAYEXPRESS</strong>. Ne pas dire
              « Payzone » : les agents ne connaissent pas ce nom et ne trouveront
              pas l'operation.
              {data?.validityDays ? (
                <div className="mt-2 flex items-center gap-1.5 text-xs">
                  <FiClock size={13} /> Code valable {data.validityDays} jour
                  {data.validityDays > 1 ? 's' : ''} a compter de maintenant.
                </div>
              ) : null}
            </div>

            {/* WhatsApp */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaWhatsapp className="text-green-600" size={18} />
                <span className="font-semibold text-sm">Envoyer le code par WhatsApp</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="212XXXXXXXXX"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={openWhatsApp}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <FaWhatsapp size={15} /> Ouvrir WhatsApp
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Le message contient le code, le montant et la marche a suivre en agence.
              </p>
            </div>

            {/* Email */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FiSend className="text-blue-600" size={18} />
                <span className="font-semibold text-sm">Envoyer le code par email</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@exemple.com"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={sendByEmail}
                  disabled={sending || !code}
                  className="px-4 py-2 rounded-lg bg-[#e11d7b] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <FiSend size={15} /> {sending ? 'Envoi…' : 'Envoyer'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Email bilingue (francais et arabe) avec le code et la liste des agences.
              </p>
            </div>

            {data?.receiptUrl ? (
              <button
                type="button"
                onClick={() => window.open(data.receiptUrl, '_blank', 'noopener')}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <FiFileText size={15} /> Imprimer le recu de reservation
              </button>
            ) : null}

            <div className="border-t pt-4 text-xs text-gray-500">
              Des que l'agence encaisse, la commande passe automatiquement en
              « payee » et les coupons sont emis. Aucune action de votre part.
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-full px-5 py-2.5 rounded-lg bg-[#e11d7b] text-white font-semibold hover:opacity-90"
            >
              Terminer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
